import {
  Activity,
  CalendarDays,
  ClipboardCheck,
  CreditCard,
  DoorOpen,
  IdCard,
  FileText,
  Database,
  HandCoins,
  History,
  Landmark,
  LayoutDashboard,
  ListTodo,
  ReceiptText,
  ScrollText,
  Settings2,
  ShieldCheck,
  Wallet,
  Truck,
  Users,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { useReports } from '@/hooks/use-cube-meta'
import type { ReportDef } from '@/lib/cube-report'
import { messages } from '@/lib/messages'
import { useAuthStore } from '@/stores/auth-store'

export interface NavItem {
  to: string
  label: string
  /**
   * Yoksa kalem her zaman görünür (rapor kalemleri: meta zaten izne göre süzülmüş gelir).
   * Dizi: izinlerden biri yeter.
   */
  permission?: string | readonly string[]
  icon: LucideIcon
  /** Menüde sayı rozeti: açık iş sayısı (`AppSidebar`). */
  badge?: 'hrTasks' | 'hrApprovals' | 'cashTodo'
}

export interface NavGroup {
  label: string
  /** Açılır grubun düğmesindeki ikon. */
  icon: LucideIcon
  items: NavItem[]
}

/**
 * Rapor view'larının `meta.nav.icon` değerleri. Bilinmeyen ad varsayılan ikona düşer.
 */
const REPORT_ICONS: Record<string, LucideIcon> = {
  truck: Truck,
  chart: LayoutDashboard,
  list: ScrollText,
  bank: Landmark,
  cheque: ReceiptText,
  card: CreditCard,
  pos: HandCoins,
  statement: FileText,
}

export function reportIcon(name: string | undefined): LucideIcon {
  return (name && REPORT_ICONS[name]) || ScrollText
}

/**
 * Sabit ekranların rota ↔ izin eşlemesi burada, tek yerde; rapor menüsü Cube
 * meta'sından kurulur (bkz. `AppSidebar.tsx`). Yetkisiz kalem menüde hiç görünmez
 * (sözleşme §4) — filtreleme `Sidebar.tsx`'te yapılır.
 */
/** İK izinleri: Süreçler sayfası herhangi birini ister (backend `require_any_hr`). */
export const HR_PERMISSIONS = [
  'hr.personnel:view',
  'hr.attendance:view',
  'hr.leave:view',
  'hr.action:run',
  'hr.request:create',
  'hr.request:approve',
  'hr.personnel:deactivate',
] as const

export const HR_REQUEST_PERMISSIONS = ['hr.request:create', 'hr.request:approve'] as const

/** İşlerim: hareket işleri giriş/çıkış, izin işleri izin ekranı izniyle görünür. */
export const HR_TASK_PERMISSIONS = ['hr.attendance:view', 'hr.leave:view'] as const

/** Yönetim bölümleri: kenar menüdeki grup ve `/yonetim` içindeki ikincil menü aynı listeyi kullanır. */
/** Yönetim izinleri: Genel bakış bunlardan biriyle açılır (backend `ADMIN_PERMISSIONS`). */
export const ADMIN_PERMISSIONS = [
  'admin.users:manage',
  'admin.groups:manage',
  'admin.audit:view',
  'admin.sources:view',
] as const

export const ADMIN_NAV_ITEMS: NavItem[] = [
  {
    to: '/yonetim/genel-bakis',
    label: messages.nav.adminOverview,
    permission: ADMIN_PERMISSIONS,
    icon: LayoutDashboard,
  },
  {
    to: '/yonetim/kullanicilar',
    label: messages.nav.adminUsers,
    permission: 'admin.users:manage',
    icon: Users,
  },
  {
    to: '/yonetim/gruplar',
    label: messages.nav.adminGroups,
    permission: 'admin.groups:manage',
    icon: ShieldCheck,
  },
  {
    to: '/yonetim/denetim',
    label: messages.nav.adminAudit,
    permission: 'admin.audit:view',
    icon: ScrollText,
  },
  {
    to: '/yonetim/kaynaklar',
    label: messages.nav.adminSources,
    permission: 'admin.sources:view',
    icon: Database,
  },
]

export const NAV_GROUPS: NavGroup[] = [
  {
    label: messages.nav.groupCash,
    icon: Wallet,
    // Sıra kullanım akışına göredir; Paralel Koşum (/nakit/paralel) menüden kalktı, rota duruyor.
    items: [
      {
        to: '/nakit',
        label: messages.nav.cashHome,
        permission: 'cash.view',
        icon: ClipboardCheck,
        badge: 'cashTodo',
      },
      {
        to: '/nakit/haftalik',
        label: messages.nav.cashWeekly,
        permission: 'cash.view',
        icon: Wallet,
      },
      {
        to: '/nakit/odeme-planlama',
        label: messages.nav.cashPlanning,
        permission: 'cash.view',
        icon: HandCoins,
      },
      {
        to: '/nakit/erp-disi',
        label: messages.nav.cashManual,
        permission: 'cash.view',
        icon: ListTodo,
      },
      {
        to: '/nakit/cekler',
        label: messages.nav.cashCheques,
        permission: 'cash.view',
        icon: ReceiptText,
      },
      {
        to: '/nakit/veri-sagligi',
        label: messages.nav.cashHealth,
        permission: 'cash.view',
        icon: Activity,
      },
      {
        to: '/nakit/odeme-listesi',
        label: messages.nav.cashPaymentList,
        permission: 'cash.view',
        icon: FileText,
      },
      {
        to: '/nakit/plan-gerceklesen',
        label: messages.nav.cashVariance,
        permission: 'cash.view',
        icon: LayoutDashboard,
      },
      {
        to: '/nakit/ayarlar',
        label: messages.nav.cashSettings,
        permission: 'cash.view',
        icon: Settings2,
      },
    ],
  },
  {
    label: messages.nav.groupHr,
    icon: IdCard,
    items: [
      {
        to: '/ik/islerim',
        label: messages.nav.hrTasks,
        permission: HR_TASK_PERMISSIONS,
        icon: ListTodo,
        badge: 'hrTasks',
      },
      {
        to: '/ik/personel',
        label: messages.nav.hrPersonnel,
        permission: 'hr.personnel:view',
        icon: IdCard,
      },
      {
        to: '/ik/giris-cikis',
        label: messages.nav.hrAttendance,
        permission: 'hr.attendance:view',
        icon: DoorOpen,
      },
      {
        to: '/ik/izinler',
        label: messages.nav.hrLeaves,
        permission: 'hr.leave:view',
        icon: CalendarDays,
      },
      {
        to: '/ik/onaylar',
        label: messages.nav.hrApprovals,
        permission: HR_REQUEST_PERMISSIONS,
        icon: ClipboardCheck,
        badge: 'hrApprovals',
      },
      {
        to: '/ik/surecler',
        label: messages.nav.hrProcesses,
        permission: HR_PERMISSIONS,
        icon: Activity,
      },
      {
        to: '/ik/mudahaleler',
        label: messages.nav.hrInterventions,
        permission: HR_PERMISSIONS,
        icon: History,
      },
    ],
  },
  {
    label: messages.nav.groupDocuments,
    icon: FileText,
    // Düzenleme düğmeleri ayrıca sayfada `can_edit`'e bağlı.
    items: [
      {
        to: '/belgeler',
        label: messages.nav.documentTemplates,
        permission: 'report.documents:view',
        icon: FileText,
      },
    ],
  },
  {
    label: messages.nav.groupSystem,
    icon: Settings2,
    items: [
      {
        to: '/sistem',
        label: messages.nav.systemStatus,
        permission: 'system.status:view',
        icon: Settings2,
      },
    ],
  },
  {
    label: messages.nav.groupAdmin,
    icon: Users,
    items: ADMIN_NAV_ITEMS,
  },
]

/** Rapor kalemleri, `meta.nav.group` sırasıyla (raporlar zaten `nav.order`'a göre sıralı). */
function reportGroups(reports: ReportDef[]): NavGroup[] {
  const groups = new Map<string, NavGroup>()
  for (const report of reports) {
    const icon = reportIcon(report.nav.icon)
    // Grup ikonu = ilk (en düşük `order`'lı) raporun ikonu.
    const group = groups.get(report.nav.group) ?? { label: report.nav.group, icon, items: [] }
    group.items.push({ to: report.route, label: report.title, icon })
    groups.set(report.nav.group, group)
  }
  return [...groups.values()]
}

/** Yetkisiz kalemler hiç dönmez (sözleşme §4: menüde görünmez). Kenar menü ve Ctrl+K paleti paylaşır. */
export function useNavGroups(): NavGroup[] {
  const identity = useAuthStore((state) => state.identity)
  const reports = useReports()

  const hasPermission = (permission: string | readonly string[] | undefined) =>
    !permission ||
    (!!identity &&
      (identity.is_superadmin ||
        (typeof permission === 'string' ? [permission] : permission).some((key) =>
          identity.permissions.includes(key),
        )))

  return [...reportGroups(reports.data ?? []), ...NAV_GROUPS]
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => hasPermission(item.permission)),
    }))
    .filter((group) => group.items.length > 0)
}
